import { restClientV2 } from '@/api/client'
import { endpoints } from '@/api/endpoints'

type Page = { page?: number; limit?: number }

const paged = (url: string, p: Page = {}) =>
  restClientV2.get(url, { params: { page: p.page ?? 1, limit: p.limit ?? 100 } }).then(r => r.data)

export interface RbacRolePayload {
  name: string
  description?: string
  city_id?: number | null
}

export interface RbacRole {
  id: number
  name: string
  description?: string | null
  city_id?: number | null
  permissions?: { id: number; name: string }[]
}

/** One row of the school-admin role→permission grid: which CRUD actions the role has on a permission. */
export interface SchoolPermissionGrant {
  role_id: number
  permission_id: number
  create: boolean
  read: boolean
  update: boolean
  delete: boolean
}

// /v2/admin/rbac/* — role CRUD, role→permissions and user→roles.
export const rbacService = {
  me:     () => restClientV2.get(endpoints.rbac.me).then(r => r.data),

  listRoles:  () => restClientV2.get(endpoints.rbac.roles).then(r => r.data),
  getRole:    (id: number) => restClientV2.get(endpoints.rbac.role(id)).then(r => r.data),
  createRole: (d: RbacRolePayload) => restClientV2.post(endpoints.rbac.roles, d).then(r => r.data),
  updateRole: (id: number, d: Partial<RbacRolePayload>) => restClientV2.put(endpoints.rbac.role(id), d).then(r => r.data),
  deleteRole: (id: number) => restClientV2.delete(endpoints.rbac.role(id)).then(r => r.data),

  // The API takes permission KEYS ('GET /admin/booking/get-bookings-list', 'GQL Query.adminGetBookingList');
  // permission_ids is accepted too but we send it empty so keys are the single source of truth.
  setRolePermissions: (id: number, permission_keys: string[], permission_ids: number[] = []) =>
    restClientV2.put(endpoints.rbac.rolePermissions(id), { permission_keys, permission_ids }).then(r => r.data),

  getUserRoles: (userId: number) => restClientV2.get(endpoints.rbac.userRoles(userId)).then(r => r.data),
  setUserRoles: (userId: number, role_ids: number[]) =>
    restClientV2.put(endpoints.rbac.userRoles(userId), { role_ids }).then(r => r.data),
}

// /v2/admin/role/* — platform-admin role, permission and user mapping.
export const roleV2Service = {
  list:           (p?: Page) => paged(endpoints.roleV2.list, p),
  createUpdate:   (d: Record<string, unknown>) => restClientV2.post(endpoints.roleV2.createUpdate, d).then(r => r.data),
  permissionList: (p?: Page) => paged(endpoints.roleV2.permissionList, p),
  mapPermissions: (d: Record<string, unknown>) => restClientV2.post(endpoints.roleV2.permissionMap, d).then(r => r.data),
  userMappingList:(p?: Page) => paged(endpoints.roleV2.userMappingList, p),
  mapUsers:       (d: { role_id: number[]; user_id: number[] }) =>
    restClientV2.post(endpoints.roleV2.userMap, d).then(r => r.data),
}

// /v2/admin/access-control-for-school-admin/* — manage school-admin roles as a platform admin.
export const schoolAdminRoleV2Service = {
  list:            (p?: Page) => paged(endpoints.roleV2SchoolAdmin.list, p),
  create:          (d: { name: string; description?: string }) =>
    restClientV2.post(endpoints.roleV2SchoolAdmin.create, d).then(r => r.data),
  // Every permission (the API caps a page at 200).
  listAllPermissions: async () => {
    const rows: unknown[] = []
    for (let page = 1; page <= 25; page++) {
      const res = await restClientV2.get(endpoints.roleV2SchoolAdmin.permissionList, { params: { page, limit: 200 } }).then(r => r.data)
      const list: unknown[] = Array.isArray(res) ? res : (res?.data ?? [])
      rows.push(...list)
      if (list.length < 200) break
    }
    return rows
  },
  permissionMapList: (roleId: number) =>
    restClientV2.get(endpoints.roleV2SchoolAdmin.permissionMapList, { params: { role_id: roleId, page: 1, limit: 200 } }).then(r => r.data),
  mapPermissions:  (d: { user_id?: number; rolePermissioMapping: SchoolPermissionGrant[] }) =>
    restClientV2.post(endpoints.roleV2SchoolAdmin.permissionMap, d).then(r => r.data),
  userMappingList: (p?: Page & { user_id?: number; role_id?: number }) =>
    restClientV2.get(endpoints.roleV2SchoolAdmin.userMappingList, { params: { page: 1, limit: 200, ...p } }).then(r => r.data),
  mapUsers:        (d: { role_id: number[]; user_id: number[] }) =>
    restClientV2.post(endpoints.roleV2SchoolAdmin.userMap, d).then(r => r.data),
}
